import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/ibm-plex-sans-arabic/400.css";
import "@fontsource/ibm-plex-sans-arabic/600.css";
import "./index.css";
import "./lib/i18n";

declare const __APP_BUILD_ID__: string;

const BUILD_ID = typeof __APP_BUILD_ID__ !== "undefined" ? __APP_BUILD_ID__ : "dev";
const BUILD_VERSION_KEY = "fn_build_version";
const UI_SCHEMA_VERSION_KEY = "fn_ui_schema_version";
const UI_SCHEMA_VERSION = "2026-04-16-cycle-aware-stats-v3";
const STALE_VIEW_STORAGE_KEYS = ["sb_dashboard_preferences"];

const isPreviewHost = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return (
    (h.includes("preview") && h.includes("lovable.app")) ||
    h.includes("lovableproject.com") ||
    h === "localhost"
  );
};

const cleanupStalePwaCaches = async () => {
  if (typeof window === "undefined") return;

  const preview = isPreviewHost();
  const savedBuild = localStorage.getItem(BUILD_VERSION_KEY);
  const savedUiSchema = localStorage.getItem(UI_SCHEMA_VERSION_KEY);
  const buildChanged = savedBuild !== BUILD_ID;
  const uiSchemaChanged = savedUiSchema !== UI_SCHEMA_VERSION;

  console.info(
    `[Fennest] host=${window.location.hostname} build=${BUILD_ID} prevBuild=${savedBuild} preview=${preview} uiSchemaChanged=${uiSchemaChanged}`
  );

  // Reset layout prefs only when we intentionally bump schema version.
  if (uiSchemaChanged) {
    STALE_VIEW_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.setItem(UI_SCHEMA_VERSION_KEY, UI_SCHEMA_VERSION);
    console.info("[Fennest] Cleared stale layout preferences");
  }

  // ALWAYS try to evict any previously-registered service worker.
  // The project no longer ships an active SW; /sw.js is a kill-switch that
  // unregisters itself on activate. Registering it forces the browser to
  // fetch the new kill-switch file even if it has an old worker cached.
  let hadStaleSW = false;
  if ("serviceWorker" in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      hadStaleSW = regs.length > 0;
      if (hadStaleSW) {
        // Force update so the kill-switch SW takes over and self-unregisters.
        await Promise.all(
          regs.map(async (r) => {
            try {
              await r.update();
            } catch {
              /* ignore */
            }
            try {
              await r.unregister();
            } catch {
              /* ignore */
            }
          })
        );
      }
    } catch {
      /* ignore */
    }
  }

  // Clear all caches (both preview and production are safe - we no longer
  // depend on Workbox precache).
  let hadCaches = false;
  if ("caches" in window) {
    try {
      const names = await caches.keys();
      hadCaches = names.length > 0;
      await Promise.all(names.map((n) => caches.delete(n)));
    } catch {
      /* ignore */
    }
  }

  localStorage.setItem(BUILD_VERSION_KEY, BUILD_ID);

  // Reload once if we actually had stale state, so the fresh shell takes over.
  const sessionKey = `fn_reloaded_${BUILD_ID}`;
  const alreadyReloaded = sessionStorage.getItem(sessionKey) === "done";
  if ((hadStaleSW || hadCaches) && !alreadyReloaded) {
    sessionStorage.setItem(sessionKey, "done");
    console.info("[Fennest] Evicted stale SW/caches, reloading for fresh state");
    window.location.reload();
  }
};

const renderApp = () => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </React.StrictMode>
  );
};

void cleanupStalePwaCaches().finally(renderApp);
